<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

// Mirrors Channel::WhatsappTest::MAX_PHONE_NUMBERS; the server has the final word.
const MAX_PHONE_NUMBERS = 10;

const { t } = useI18n();
const store = useStore();

const newPhoneNumber = ref('');
const isSaving = ref(false);

// The list comes from the saved inbox: the server normalizes it, and the store refreshes the
// inbox after each save, so the screen shows what was actually stored.
const phoneNumbers = computed(() => props.inbox.phone_numbers || []);
const sharedPhoneNumber = computed(() => props.inbox.shared_phone_number);
const isFull = computed(() => phoneNumbers.value.length >= MAX_PHONE_NUMBERS);
const canAdd = computed(
  () => !isFull.value && !isSaving.value && newPhoneNumber.value.trim() !== ''
);

const savePhoneNumbers = async list => {
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: { phone_numbers: list },
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.SUCCESS_MESSAGE'));
    return true;
  } catch (error) {
    useAlert(error.message);
    return false;
  } finally {
    isSaving.value = false;
  }
};

const addPhoneNumber = async () => {
  if (!canAdd.value) return;

  const saved = await savePhoneNumbers([
    ...phoneNumbers.value,
    newPhoneNumber.value.trim(),
  ]);
  if (saved) newPhoneNumber.value = '';
};

const removePhoneNumber = phoneNumber =>
  savePhoneNumbers(phoneNumbers.value.filter(item => item !== phoneNumber));
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.TITLE')"
    :sub-title="$t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.SUBTITLE')"
    :show-border="false"
  >
    <div class="flex flex-col gap-6">
      <div
        v-if="sharedPhoneNumber"
        data-test-id="shared-phone-number"
        class="p-4 rounded-xl bg-n-alpha-2 outline outline-1 outline-n-weak"
      >
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.SEND_TO') }}
        </p>
        <p class="mb-0 text-lg font-medium text-n-slate-12">
          {{ sharedPhoneNumber }}
        </p>
      </div>
      <div
        v-else
        data-test-id="shared-phone-number-missing"
        class="p-4 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.SHARED_NUMBER_MISSING') }}
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.LIST_TITLE') }}
          </p>
          <span
            data-test-id="phone-numbers-count"
            class="text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.COUNT', {
                count: phoneNumbers.length,
                max: MAX_PHONE_NUMBERS,
              })
            }}
          </span>
        </div>
        <p v-if="!phoneNumbers.length" class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.EMPTY') }}
        </p>
        <ul v-else class="flex flex-col gap-2 m-0 list-none">
          <li
            v-for="phoneNumber in phoneNumbers"
            :key="phoneNumber"
            data-test-id="phone-number"
            class="flex items-center justify-between px-3 py-2 rounded-lg outline outline-1 outline-n-weak"
          >
            <span class="text-sm text-n-slate-12">{{ phoneNumber }}</span>
            <NextButton
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="sm"
              :title="$t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.REMOVE')"
              :disabled="isSaving"
              data-test-id="remove-phone-number"
              @click="removePhoneNumber(phoneNumber)"
            />
          </li>
        </ul>
      </div>

      <div class="flex items-start gap-2">
        <Input
          v-model="newPhoneNumber"
          class="flex-1"
          :placeholder="
            $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.INPUT_PLACEHOLDER')
          "
          :disabled="isFull"
          :message="
            isFull ? $t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.LIMIT_REACHED') : ''
          "
          @enter="addPhoneNumber"
        />
        <NextButton
          :label="$t('INBOX_MGMT.WHATSAPP_TEST_NUMBERS.ADD')"
          :disabled="!canAdd"
          :is-loading="isSaving"
          data-test-id="add-phone-number"
          @click="addPhoneNumber"
        />
      </div>
    </div>
  </SettingsSection>
</template>
